
import { useMemo } from "react";
import { Candidate } from "@/types/candidate";
import { SortField, SortOrder } from "@/components/candidates/CandidatesViewOptions";

export const ITEMS_PER_PAGE = 25;

export function useCandidatesFiltering(
  candidates: Candidate[],
  searchTerm: string,
  skillsFilter: string[],
  statusFilter: string[],
  functionFilter: string,
  sortField: SortField,
  sortOrder: SortOrder,
  currentPage: number
) {
  // Filter and sort candidates
  const filteredCandidates = useMemo(() => {
    const lowerSearchTerm = searchTerm.toLowerCase();
    const lowerFunctionFilter = functionFilter.toLowerCase();
    const lowerStatusFilter = statusFilter.map(s => s.toLowerCase());
    
    // Filter by search term, skills, status, and function
    const filtered = candidates.filter(candidate => {
      // Search term filter - case insensitive
      const matchesSearch = 
        lowerSearchTerm === "" || 
        candidate.name.toLowerCase().includes(lowerSearchTerm) ||
        candidate.email.toLowerCase().includes(lowerSearchTerm) ||
        (candidate.function && candidate.function.toLowerCase().includes(lowerSearchTerm));
      
      // Skills filter - case insensitive
      const matchesSkills = 
        skillsFilter.length === 0 || 
        (candidate.skills && 
          skillsFilter.every(skill => 
            candidate.skills.some(candidateSkill => 
              candidateSkill.toLowerCase() === skill.toLowerCase()
            )
          )
        );
      
      // Status filter - ensure lowercase comparison
      const matchesStatus = 
        statusFilter.length === 0 || 
        (candidate.status && lowerStatusFilter.includes(candidate.status.toLowerCase()));
      
      // Function filter - case insensitive
      const matchesFunction = 
        functionFilter === "" || 
        (candidate.function && candidate.function.toLowerCase().includes(lowerFunctionFilter));
      
      return matchesSearch && matchesSkills && matchesStatus && matchesFunction;
    });

    // Sort by selected field
    return [...filtered].sort((a, b) => {
      let comparison = 0;
      
      switch (sortField) {
        case "name":
          comparison = a.name.localeCompare(b.name);
          break;
        case "dateCreated":
          // Using ID as a proxy for creation date since we don't have timestamps
          comparison = a.id.localeCompare(b.id);
          break;
        case "matchScore":
          const scoreA = a.match_score || 0;
          const scoreB = b.match_score || 0;
          comparison = scoreA - scoreB;
          break;
      }
      
      return sortOrder === "asc" ? comparison : -comparison;
    });
  }, [
    candidates, 
    searchTerm, 
    sortField, 
    sortOrder, 
    skillsFilter, 
    statusFilter, 
    functionFilter
  ]);

  // Paginate the filtered candidates
  const paginatedCandidates = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredCandidates.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredCandidates, currentPage]);

  const totalPages = Math.ceil(filteredCandidates.length / ITEMS_PER_PAGE);

  return {
    filteredCandidates,
    paginatedCandidates,
    totalPages
  };
}
